import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { ReactComponent as DropdownIcon } from "../../assets/Vector 474 (Stroke).svg";
import CommonSelect from "../common/CommonSelect";

import { DateTime } from "../opportunities/OpportunitiesTable";

const RECIPIENT_TYPES = [
  { value: "provider", label: "Provider" },
  { value: "pharmacy", label: "Pharmacy" },
  { value: "member", label: "Member" },
  { value: "others", label: "Others" },
];

const LINKED_MEMBERS = [
  { id: "mem1", name: "John Doe" },
  { id: "mem2", name: "Jane Smith" },
  { id: "mem3", name: "Robert Johnson" },
];

const limitDetails = (val = "") => (val.length > 512 ? val.slice(0, 512) : val);

const OUTREACH_REASON_OPTIONS_BY_TYPE = {
  member: [
    { value: "SCHEDULE_CALLBACK", label: "Schedule Callback" },
    { value: "MEMBER_FOLLOW_UP", label: "Member Follow-up" },
  ],
  others: [
    { value: "SCHEDULE_CALLBACK", label: "Schedule Callback" },
    { value: "MEMBER_FOLLOW_UP", label: "Member Follow-up" },
  ],
  pharmacy: [
    { value: "REQUEST_PRESCRIPTION", label: "Request Prescription" },
    { value: "CONFIRM_RX_RECEIVED", label: "Confirm Rx Received" },
    { value: "PHARMACY_CONSULTATION", label: "Pharmacy Consultation" },
  ],
  provider: [
    { value: "PROVIDER_CONSULTATION", label: "Provider consultation" },
    { value: "PROVIDER_EDUCATION", label: "Provider Education" },
    { value: "PROVIDER_FOLLOW_UP", label: "Provider Follow-up" },
    { value: "DOSAGE_OPTIMISATION", label: "Dosing Optimization" },
  ],
};

const normalizeOutreachReason = (raw, recipientType) => {
  if (!raw) return "";
  const typeKey = (recipientType || "").toLowerCase();
  const options = OUTREACH_REASON_OPTIONS_BY_TYPE[typeKey] || [];
  const trimmed = raw.trim();
  const upper = trimmed.toUpperCase();

  const direct = options.find((o) => o.value === upper);
  if (direct) return direct.value;

  const labelMatch = options.find(
    (o) => o.label.toLowerCase() === trimmed.toLowerCase(),
  );
  if (labelMatch) return labelMatch.value;

  return upper.replaceAll(/\s+/g, "_");
};

const dateOnly = (val) => {
  if (!val) return "";
  if (val instanceof Date) return val.toISOString().split("T")[0];
  if (typeof val === "string") {
    return val.includes("T") ? val.split("T")[0] : val;
  }
  return "";
};

const TaskDropdownButton = ({
  field,
  value,
  options,
  placeholder,
  readOnly = false,
  buttonRef,
  onOpen,
}) => {
  const selectedOption = options.find(
    (opt) => opt.value === value || opt.id === value,
  );
  const selectedLabel = selectedOption?.label || "";

  const valueColor = (() => {
    if (!value) {
      return "#999893";
    } else if (field === "outreachReason") {
      return "#111827";
    } else {
      return "#999893";
    }
  })();

  const getFieldLabel = (fieldName) => {
    if (fieldName === "recipientType") {
      return "Recipient type";
    } else if (fieldName === "linkedMember") {
      return "Linked member";
    } else if (fieldName === "outreachReason") {
      return "Outreach reason";
    } else {
      return fieldName;
    }
  };

  const ariaLabel = value
    ? `${getFieldLabel(field)}: ${selectedLabel}`
    : `${getFieldLabel(field)}: ${placeholder}`;

  return (
    <Button
      ref={buttonRef}
      onClick={(e) => !readOnly && onOpen(field, e)}
      aria-label={ariaLabel}
      disabled={readOnly}
      sx={{
        width: "100%",
        minWidth: "200px",
        height: "40px",
        border: "1px solid #E9E9E8",
        borderRadius: "8px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "0 12px",
        backgroundColor: readOnly ? "#E8E8E9" : "#FFFFFF",
        textTransform: "none",
        color: valueColor,
        fontSize: "14px",
        "&:hover": {
          backgroundColor: readOnly ? "#E8E8E9" : "#FFFFFF",
          cursor: readOnly ? "not-allowed" : "pointer",
        },
      }}
    >
      <span
        style={{
          fontWeight: value ? "normal" : "400",
          color: valueColor,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          width: "100%",
          textAlign: "left",
        }}
      >
        {value ? selectedLabel : placeholder}
      </span>
      {!readOnly && (
        <DropdownIcon style={{ marginLeft: "8px", flexShrink: 0 }} />
      )}
    </Button>
  );
};

TaskDropdownButton.propTypes = {
  field: PropTypes.string.isRequired,
  value: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      id: PropTypes.string,
      label: PropTypes.string,
    }),
  ).isRequired,
  placeholder: PropTypes.string,
  readOnly: PropTypes.bool,
  buttonRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any }),
  ]),
  onOpen: PropTypes.func.isRequired,
};

const RecipientDetailsSection = ({
  formData,
  isEditMode,
  linkedMembers,
  handleInputChange,
  handleOpenSelect,
  filterButtonRefs,
  handleCloseSelect,
  anchorEl,
}) => {
  return (
    <Box
      sx={{
        overflow: "hidden",
        mb: 1,
        bgcolor: "#FFFFFF",
      }}
    >
      <Box
        sx={{
          p: 2,
          bgcolor: "transparent",
          width: "100%",
          "& .MuiGrid-container": { width: "100%", margin: 0 },
        }}
      >
        <Typography
          variant="subtitle1"
          fontWeight="bold"
          mb={2}
          fontSize={16}
          sx={{ textAlign: "left", pl: 1 }}
        >
          Recipient Details
        </Typography>
        <Box
          sx={{
            bgcolor: "transparent",
            p: 1,
            width: "100%",
            "& .MuiFormControl-root": {
              width: "100%",
              maxWidth: "100%",
              display: "flex",
              flexDirection: "column",
              gap: "10px",
              "& .MuiOutlinedInput-root": {
                height: "40px",
                borderRadius: "8px",
                backgroundColor: "#FFFFFF",
                "& fieldset": { border: "none" },
                "&.Mui-focused fieldset": { border: "none" },
                "&.Mui-error fieldset": { border: "none" },
              },
              "& .MuiInputLabel-root": {
                position: "relative",
                transform: "none",
                marginBottom: "4px",
                fontSize: "0.875rem",
                color: "text.primary",
                "&.Mui-focused, &.MuiFormLabel-filled": {
                  transform: "none",
                  color: "text.primary",
                },
              },
              "& .MuiInputBase-input": {
                padding: "16px",
                height: "100%",
                boxSizing: "border-box",
              },
              "& .MuiSelect-select": {
                padding: "16px",
                display: "flex",
                alignItems: "center",
              },
              "& .MuiOutlinedInput-notchedOutline": { border: "none" },
              "& .MuiSelect-icon": { right: "16px" },
            },
          }}
        >
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: "24px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: "24px",
                width: "100%",
              }}
            >
              <Box sx={{ width: { xs: "100%", sm: "calc(50% - 12px)" } }}>
                <Typography
                  variant="subtitle2"
                  color="#403C6F"
                  fontWeight={500}
                  fontSize={14}
                  gutterBottom
                >
                  Recipient Type
                </Typography>
                <TaskDropdownButton
                  field="recipientType"
                  value={formData.recipientType}
                  options={RECIPIENT_TYPES}
                  placeholder="Select the type of recipient"
                  readOnly={true}
                  buttonRef={filterButtonRefs.recipientType}
                  onOpen={handleOpenSelect}
                />
              </Box>
              <Box sx={{ width: { xs: "100%", sm: "calc(50% - 12px)" } }}>
                <Typography
                  variant="subtitle2"
                  color="#403C6F"
                  fontWeight={500}
                  fontSize={14}
                  gutterBottom
                >
                  Recipient Name
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Name of recipient"
                  value={formData.recipientName}
                  size="medium"
                  disabled
                  sx={{
                    backgroundColor: "#E8E8E9 !important",
                    borderRadius: "8px",
                    "& .MuiOutlinedInput-root.Mui-disabled": {
                      backgroundColor: "#E8E8E9 !important",
                      borderRadius: "8px",
                      "& .MuiOutlinedInput-input": {
                        WebkitTextFillColor: "#999893",
                        color: "#999893",
                      },
                    },
                  }}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <SearchIcon sx={{ color: "#999893" }} />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </Box>
            </Box>

            <Box sx={{ width: "100%", maxWidth: "100%" }}>
              {formData.recipientType === "provider" ||
              formData.recipientType === "pharmacy" ? (
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "24px",
                    "& > div": { width: { xs: "100%", sm: "100%" } },
                  }}
                >
                  <Box>
                    <Typography
                      variant="subtitle2"
                      color="#403C6F"
                      fontWeight={500}
                      fontSize={14}
                      gutterBottom
                    >
                      Select Linked Member
                    </Typography>
                    <Box>
                      <Box
                        sx={{
                          backgroundColor: isEditMode
                            ? "#E8E8E9"
                            : "transparent",
                          borderRadius: "8px",
                          "& .Mui-disabled": {
                            backgroundColor: "#E8E8E9 !important",
                            borderRadius: "8px",
                            "& .MuiOutlinedInput-input": {
                              WebkitTextFillColor: "#999893",
                              color: "#999893",
                            },
                          },
                        }}
                      >
                        <TaskDropdownButton
                          field="linkedMember"
                          value={formData.linkedMember}
                          options={linkedMembers.map((m) => ({
                            value: m.id,
                            label: m.name,
                          }))}
                          placeholder="Select linked member"
                          readOnly={isEditMode}
                          buttonRef={filterButtonRefs.linkedMember}
                          onOpen={handleOpenSelect}
                        />
                        <CommonSelect
                          id="linked-member-select"
                          open={Boolean(anchorEl.linkedMember) && !isEditMode}
                          anchorEl={anchorEl.linkedMember}
                          onClose={() => handleCloseSelect("linkedMember")}
                          inQuickview={false}
                          filterButtonRef={filterButtonRefs.linkedMember}
                          noteTypes={linkedMembers.map((m) => m.name)}
                          selectedTypes={
                            formData.linkedMember
                              ? [
                                  linkedMembers.find(
                                    (m) => m.id === formData.linkedMember,
                                  )?.name || "",
                                ]
                              : []
                          }
                          handleTypeToggle={(type) => {
                            const selected = linkedMembers.find(
                              (m) => m.name === type,
                            );
                            if (selected) {
                              handleInputChange("linkedMember", selected.id);
                            }
                          }}
                          multiple={false}
                          width={
                            filterButtonRefs.linkedMember.current
                              ? `${filterButtonRefs.linkedMember.current.offsetWidth}px`
                              : "auto"
                          }
                          disabled={isEditMode}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Box>
              ) : null}
            </Box>

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: "24px",
                width: "100%",
              }}
            >
              <Box sx={{ width: { xs: "100%", sm: "calc(50% - 12px)" } }}>
                <Typography
                  variant="subtitle2"
                  color="#403C6F"
                  fontWeight={500}
                  fontSize={14}
                  gutterBottom
                >
                  Timezone
                </Typography>
                <TextField
                  fullWidth
                  placeholder=""
                  value={formData.timeZone || "N/A"}
                  size="medium"
                  disabled
                  slotProps={{ htmlInput: { "aria-label": "Time zone" } }}
                  sx={{
                    backgroundColor: "#E8E8E9 !important",
                    borderRadius: "8px",
                    "& .MuiOutlinedInput-root.Mui-disabled": {
                      backgroundColor: "#E8E8E9 !important",
                      borderRadius: "8px",
                      "& .MuiOutlinedInput-input": {
                        WebkitTextFillColor: "#999893",
                        color: "#999893",
                      },
                    },
                  }}
                />
              </Box>
              <Box sx={{ width: { xs: "100%", sm: "calc(50% - 12px)" } }}>
                <Typography
                  variant="subtitle2"
                  color="#403C6F"
                  fontWeight={500}
                  fontSize={14}
                  gutterBottom
                >
                  Language
                </Typography>
                <TextField
                  fullWidth
                  placeholder=""
                  value={formData.language || "N/A"}
                  size="medium"
                  disabled
                  slotProps={{ htmlInput: { "aria-label": "Language" } }}
                  sx={{
                    backgroundColor: "#E8E8E9 !important",
                    borderRadius: "8px",
                    "& .MuiOutlinedInput-root.Mui-disabled": {
                      backgroundColor: "#E8E8E9 !important",
                      borderRadius: "8px",
                      "& .MuiOutlinedInput-input": {
                        WebkitTextFillColor: "#999893",
                        color: "#999893",
                      },
                    },
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

RecipientDetailsSection.propTypes = {
  formData: PropTypes.object.isRequired,
  isEditMode: PropTypes.bool.isRequired,
  linkedMembers: PropTypes.array.isRequired,
  handleInputChange: PropTypes.func.isRequired,
  handleOpenSelect: PropTypes.func.isRequired,
  filterButtonRefs: PropTypes.object.isRequired,
  handleCloseSelect: PropTypes.func.isRequired,
  anchorEl: PropTypes.object.isRequired,
};

const TaskDetailsSection = ({
  formData,
  handleInputChange,
  handleOpenSelect,
  filterButtonRefs,
  handleCloseSelect,
  anchorEl,
  detailsError,
}) => {
  const getOutreachReasonOptions = () => {
    return OUTREACH_REASON_OPTIONS_BY_TYPE[formData.recipientType] || [];
  };

  return (
    <Box
      sx={{
        p: 2,
        bgcolor: "#FFFFFF",
        width: "100%",
        "& .MuiGrid-container": { width: "100%", margin: 0 },
      }}
    >
      <Typography
        variant="h6"
        fontWeight="bold"
        mb={1}
        fontSize={16}
        sx={{ textAlign: "left", pl: 1 }}
      >
        Task Details
      </Typography>
      <Box sx={{ bgcolor: "transparent", p: 1, width: "100%" }}>
        <Box
          sx={{ display: "flex", flexWrap: "wrap", gap: "24px", width: "100%" }}
        >
          <Box sx={{ width: { xs: "100%", sm: "calc(50% - 12px)" } }}>
            <Typography
              variant="subtitle2"
              color="#403C6F"
              fontWeight={500}
              fontSize={14}
              gutterBottom
            >
              Due Date
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DesktopDatePicker
                value={formData.dueDate}
                onChange={(newValue) => handleInputChange("dueDate", newValue)}
                minDate={new Date()}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    size="small"
                    error={false}
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        sx: {
                          height: "40px",
                          "& input": {
                            padding: "8px 14px",
                            color: formData.dueDate ? "#111827" : "#6B7280",
                            "&::placeholder": { color: "#6B7280" },
                          },
                        },
                      },
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": { borderColor: "#E9E9E8 !important" },
                        "&:hover fieldset": {
                          borderColor: "#E9E9E8 !important",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#E9E9E8 !important",
                        },
                        "&.Mui-error fieldset": {
                          borderColor: "#E9E9E8 !important",
                        },
                      },
                    }}
                  />
                )}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    error: false,
                    slotProps: {
                      textField: {
                        slotProps: {
                          input: {
                            sx: {
                              height: "40px",
                              "& input": {
                                padding: "8px 14px",
                                color: formData.dueDate ? "#111827" : "#6B7280",
                                "&::placeholder": { color: "#6B7280" },
                              },
                            },
                          },
                        },
                      },
                    },
                    sx: {
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": { borderColor: "#E9E9E8 !important" },
                        "&:hover fieldset": {
                          borderColor: "#E9E9E8 !important",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#E9E9E8 !important",
                        },
                        "&.Mui-error fieldset": {
                          borderColor: "#E9E9E8 !important",
                        },
                      },
                    },
                  },
                }}
              />
            </LocalizationProvider>
          </Box>
          <Box sx={{ width: { xs: "100%", sm: "calc(50% - 12px)" } }}>
            <Typography
              variant="subtitle2"
              color="#403C6F"
              fontWeight={500}
              fontSize={14}
              gutterBottom
            >
              Outreach Reason
            </Typography>
            <Box>
              <TaskDropdownButton
                field="outreachReason"
                value={formData.outreachReason}
                options={getOutreachReasonOptions()}
                placeholder="Select Outreach Reason"
                buttonRef={filterButtonRefs.outreachReason}
                onOpen={handleOpenSelect}
              />
              <CommonSelect
                id="outreach-reason-select"
                open={Boolean(anchorEl.outreachReason)}
                anchorEl={anchorEl.outreachReason}
                onClose={() => handleCloseSelect("outreachReason")}
                inQuickview={false}
                filterButtonRef={filterButtonRefs.outreachReason}
                noteTypes={getOutreachReasonOptions().map((opt) => opt.label)}
                selectedTypes={
                  formData.outreachReason
                    ? [
                        getOutreachReasonOptions().find(
                          (t) => t.value === formData.outreachReason,
                        )?.label || "",
                      ]
                    : []
                }
                handleTypeToggle={(type) => {
                  const selected = getOutreachReasonOptions().find(
                    (t) => t.label === type,
                  );
                  if (selected) {
                    handleInputChange("outreachReason", selected.value);
                  }
                }}
                multiple={false}
                width={
                  filterButtonRefs.outreachReason.current
                    ? `${filterButtonRefs.outreachReason.current.offsetWidth}px`
                    : "auto"
                }
              />
            </Box>
          </Box>
          <Box sx={{ width: "100%", maxWidth: "100%" }}>
            <Typography
              variant="subtitle2"
              color="#403C6F"
              fontWeight={500}
              fontSize={14}
              gutterBottom
            >
              Task Note
            </Typography>
            <Box sx={{ position: "relative" }}>
              <TextField
                fullWidth
                multiline
                rows={2}
                placeholder="Enter task note here"
                value={formData.details}
                onChange={(e) => handleInputChange("details", e.target.value)}
                size="medium"
                error={!!detailsError}
                helperText={detailsError || undefined}
                slotProps={{ htmlInput: { maxLength: 512 } }}
                sx={{
                  backgroundColor: "#FFFFFF",
                  "& .MuiOutlinedInput-root": {
                    "& textarea::placeholder": { color: "#999893" },
                    padding: "8px 14px",
                    "& textarea": { minHeight: "100px" },
                    "& fieldset": {
                      borderColor: "#E9E9E8",
                      borderRadius: "8px",
                    },
                    "&:hover fieldset": { borderColor: "#E9E9E8" },
                    "&.Mui-focused fieldset": { borderColor: "#E9E9E8" },
                    "&.Mui-disabled": {
                      "& fieldset": {
                        borderColor: "#E9E9E8",
                        borderRadius: "8px",
                      },
                      "& .MuiInputBase-input": {
                        WebkitTextFillColor: "#999893",
                        color: "#999893",
                      },
                    },
                  },
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  position: "absolute",
                  bottom: 6,
                  right: 12,
                  bgcolor: "rgba(255,255,255,0.8)",
                  px: 0.5,
                  borderRadius: "4px",
                  color:
                    formData.details.length > 512 || detailsError
                      ? "#d32f2f"
                      : "#5A5875",
                  pointerEvents: "none",
                }}
              >
                {formData.details.length}/512
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

TaskDetailsSection.propTypes = {
  formData: PropTypes.object.isRequired,
  handleInputChange: PropTypes.func.isRequired,
  handleOpenSelect: PropTypes.func.isRequired,
  filterButtonRefs: PropTypes.object.isRequired,
  handleCloseSelect: PropTypes.func.isRequired,
  anchorEl: PropTypes.object.isRequired,
  detailsError: PropTypes.string,
};

const RecentActivitySection = ({ formData }) => {
  return (
    <Box
      sx={{
        p: 2,
        bgcolor: "#FFFFFF",
        width: "100%",
        "& .MuiGrid-container": { width: "100%", margin: 0 },
      }}
    >
      <Typography
        variant="subtitle1"
        fontWeight="bold"
        mb={1}
        fontSize={16}
        sx={{ textAlign: "left", pl: 1 }}
      >
        Most Recent Activity
      </Typography>
      <Box
        sx={{
          bgcolor: "transparent",
          p: 1,
          width: { xs: "100%", sm: "30%" },
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            display: "inline-flex",
            flexDirection: "column",
            alignItems: "flex-start",
            justifyContent: "center",
            bgcolor: "#F7F8FB",
            px: 1.5,
            py: 0.75,
            borderRadius: 1,
            minHeight: "40px",
            gap: 0.25,
          }}
        >
          {formData.recentActivityStatus || formData.recentActivityAt ? (
            <>
              {formData.recentActivityStatus && (
                <Box
                  sx={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: formData.recentActivityStatusColor || "#6c757d",
                    lineHeight: 1.2,
                  }}
                >
                  {formData.recentActivityStatus}
                </Box>
              )}
              {formData.recentActivityAt && (
                <Box
                  sx={{
                    fontSize: 12,
                    fontWeight: 400,
                    color: "#403C6F",
                    lineHeight: 1.2,
                  }}
                >
                  {DateTime(formData.recentActivityAt)}
                </Box>
              )}
            </>
          ) : (
            <Box sx={{ fontSize: 12, fontWeight: 600, color: "#403C6F" }}>
              N/A
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

RecentActivitySection.propTypes = {
  formData: PropTypes.object.isRequired,
};

export default function TaskCreationModal({
  open,
  onClose,
  task = null,
  onSave,
}) {
  const isEditMode = !!task;

  const [linkedMembers, setLinkedMembers] = useState(LINKED_MEMBERS);

  const [anchorEl, setAnchorEl] = useState({
    recipientType: null,
    language: null,
    timeZone: null,
    outreachReason: null,
    linkedMember: null,
    program: null,
  });

  const isFormValid = () => {
    const currentDate = dateOnly(formData.dueDate);
    const originalDate = task ? dateOnly(task.dueDate) : dateOnly(new Date());
    const originalOutreach = task
      ? normalizeOutreachReason(task.outreachReason, task.recipientType)
      : "";
    return (
      formData.dueDate &&
      formData.outreachReason &&
      formData.details !== undefined &&
      (currentDate !== originalDate ||
        formData.outreachReason !== originalOutreach ||
        formData.details !== (task?.details || task?.note || ""))
    );
  };

  const handleSave = () => {
    if (isFormValid() && onSave) {
      const formattedData = {
        ...formData,
        recipientType: formData.recipientType.toUpperCase(),
        recipientId: formData.recipientId || null,
        linkedMemberId: formData.linkedMember || null,
        programId: formData.program || null,
        language: formData.language || null,
        dueDate: formData.dueDate,
        outreachReason: formData.outreachReason,
        note: formData.details || "",
      };

      onSave(formattedData);
    }
  };

  const filterButtonRefs = {
    recipientType: useRef(null),
    language: useRef(null),
    timeZone: useRef(null),
    outreachReason: useRef(null),
    linkedMember: useRef(null),
    program: useRef(null),
  };

  const [formData, setFormData] = useState({
    recipientType: "",
    recipientName: "",
    recipientId: "",
    timeZone: "",
    language: "",
    dueDate: new Date(),
    outreachReason: "",
    details: "",
    linkedMember: "",
    program: "",
  });
  const [detailsError, setDetailsError] = useState("");

  const getInitialState = React.useCallback((taskItem) => {
    if (taskItem) {
      let initialLinkedMembers = LINKED_MEMBERS;
      if (
        taskItem.linkedMemberName &&
        (taskItem.linkedMember || taskItem.linkedMemberId)
      ) {
        initialLinkedMembers = [
          {
            id: taskItem.linkedMember || taskItem.linkedMemberId,
            name: taskItem.linkedMemberName,
          },
        ];
      } else if (
        (taskItem.recipientType === "provider" ||
          taskItem.recipientType === "pharmacy") &&
        taskItem.recipientId &&
        taskItem.recipientName &&
        taskItem.linkedMemberName &&
        taskItem.linkedMemberId
      ) {
        initialLinkedMembers = [
          { id: taskItem.linkedMemberId, name: taskItem.linkedMemberName },
        ];
      }
      return {
        initialFormData: {
          recipientType: taskItem.recipientType?.toLowerCase() || "",
          recipientName: taskItem.recipientName || "",
          recipientId: taskItem.recipientId || "",
          timeZone: taskItem.timeZone || taskItem.timezone || "",
          language: taskItem.language,
          dueDate: taskItem.dueDate ? new Date(taskItem.dueDate) : new Date(),
          outreachReason: normalizeOutreachReason(
            taskItem.outreachReason,
            taskItem.recipientType?.toLowerCase() || "",
          ),
          details: taskItem.details || taskItem.note || "",
          linkedMember: taskItem.linkedMember || taskItem.linkedMemberId || "",
          program: taskItem.program || taskItem.programId || "",
          recentActivityStatus: taskItem.recentActivityStatus || "",
          recentActivityAt: taskItem.recentActivityAt || "",
          recentActivityStatusColor:
            taskItem.recentActivityStatusColor ||
            taskItem.recentActivity?.colourCode ||
            "",
        },
        initialLinkedMembers,
      };
    }
    return {
      initialFormData: {
        recipientType: "member",
        recipientName: "",
        recipientId: "",
        timeZone: "",
        language: "",
        dueDate: new Date(),
        outreachReason: "",
        details: "",
        linkedMember: "",
        program: "",
      },
      initialLinkedMembers: LINKED_MEMBERS,
    };
  }, []);

  useEffect(() => {
    const { initialFormData, initialLinkedMembers } = getInitialState(task);
    setFormData(initialFormData);
    setLinkedMembers(initialLinkedMembers);
  }, [task, getInitialState]);

  useEffect(() => {}, [formData]);

  const handleOpenSelect = (field, event) => {
    if (
      field === "recipientType" ||
      field === "timeZone" ||
      field === "language"
    ) {
      return;
    }
    setAnchorEl((prev) => ({ ...prev, [field]: event.currentTarget }));
  };

  const handleCloseSelect = (field) => {
    setAnchorEl((prev) => ({ ...prev, [field]: null }));
  };

  const handleInputChange = (field, value) => {
    const newFormData = {
      ...formData,
      [field]: value,
    };

    if (field === "recipientType") {
      if (value !== "provider") {
        newFormData.linkedMember = "";
        newFormData.program = "";
      }
    }

    if (field === "details") {
      newFormData.details = limitDetails(value || "");
      const trimmed = (newFormData.details || "").trim();
      setDetailsError(trimmed ? "" : "Task note is required");
    }
    setFormData(newFormData);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      aria-labelledby="edit-task-dialog-title"
      ModalProps={{
        keepMounted: false,
        disableEnforceFocus: false,
        disableAutoFocus: false,
        disableRestoreFocus: false,
      }}
      slotProps={{
        paper: {
          sx: {
            borderRadius: 2,
            bgcolor: "#F7F8FB",
            maxWidth: "700px",
            width: "calc(100% - 80px)",
            border: "1px solid #E9E9E8",
            boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.05)",
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
            "& .MuiDialogContent-root": {
              padding: 0,
              overflowY: "auto",
              flex: 1,
              maxHeight: "none",
              marginBottom: "60px",
            },
            "& .MuiDialogActions-root": {
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
            },
          },
        },
      }}
    >
      <DialogTitle
        id="edit-task-dialog-title"
        sx={{
          fontWeight: "bold",
          fontSize: "1.5rem",
          py: 3,
          px: 3,
          m: 0,
          pb: 1,
          textAlign: "left",
          pl: 3,
        }}
      >
        {isEditMode ? "Edit Task" : "Task Creation"}
        <IconButton
          aria-label={
            isEditMode ? "Close edit task dialog" : "Close new task dialog"
          }
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 0, px: 1, pb: 1 }}>
        <Box
          sx={{
            px: 1,
            pb: 1,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            margin: 1,
            bgcolor: "#FFFFFF",
            borderRadius: 2,
            border: "1px solid #E9E9E8",
          }}
        >
          <RecipientDetailsSection
            formData={formData}
            isEditMode={isEditMode}
            linkedMembers={linkedMembers}
            handleInputChange={handleInputChange}
            handleOpenSelect={handleOpenSelect}
            filterButtonRefs={filterButtonRefs}
            handleCloseSelect={handleCloseSelect}
            anchorEl={anchorEl}
          />

          <RecentActivitySection formData={formData} />

          <TaskDetailsSection
            formData={formData}
            handleInputChange={handleInputChange}
            handleOpenSelect={handleOpenSelect}
            filterButtonRefs={filterButtonRefs}
            handleCloseSelect={handleCloseSelect}
            anchorEl={anchorEl}
            detailsError={detailsError}
          />
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          justifyContent: "flex-end",
          gap: "10px",
          p: 2,
          position: "relative",
          zIndex: 0,
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            height: 38,
            gap: "10px",
            opacity: 1,
            borderRadius: "52px",
            border: "1px solid #9F9DB6",
            color: "#403C6F",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "rgba(64, 60, 111, 0.04)",
              borderColor: "#6E6B7B",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!isFormValid()}
          sx={{
            height: 38,
            gap: "10px",
            opacity: 1,
            borderRadius: "52px",
            backgroundColor: isFormValid() ? "#3F3D56" : "#E9E9E8",
            color: isFormValid() ? "#FFFFFF" : "#999893",
            textTransform: "none",
            "&:hover": {
              backgroundColor: isFormValid() ? "#2F2C46" : "#E9E9E8",
            },
            "&.Mui-disabled": {
              backgroundColor: "#E9E9E8",
              color: "#999893",
            },
          }}
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
}

TaskCreationModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  task: PropTypes.object,
  onSave: PropTypes.func,
};
